import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { Result } from '@/domain/protocols/Result'
import { AuthState } from '@/domain/states/AuthState'

import { NEXATLAS_API_BASE } from '../apiBaseUrls'
import type { IHttpClient } from '../IHttpClient'
import { IUserChartsWebClient, UserChartSyncRequest, UserChartSyncResponse } from './IUserChartsWebClient'

export const USER_CHARTS_SYNC_TIMEOUT_MS = 30000
// Processed tiles archives can be large (ENRC charts), so the upload gets a
// generous timeout, matching the pilotapp client.
export const USER_CHARTS_UPLOAD_TIMEOUT_MS = 600000

type UserChartsErrorBody = { error?: string }

@injectable()
export class UserChartsWebClient implements IUserChartsWebClient {
  constructor(
    @inject(InjectionTokens.HttpClient) private httpClient: IHttpClient,
    @inject(InjectionTokens.AuthState) private authState: AuthState
  ) {}

  protected getRemoteUrl(): string {
    // Same-origin path in every environment: dev proxies it to VITE_NEXATLAS_API_URL
    // via Vite (vite.config.ts), and built environments proxy it via the frontend
    // nginx (deployment/docker/nginx.conf). Keeping the browser request same-origin
    // lets the server hold the long-lived processed-tiles upload/download connection,
    // so large transfers are not aborted by the browser with ERR_NETWORK_CHANGED on
    // flaky/multi-homed networks (mirrors ChartConverterWebClient / the /pilotapp-bff proxy).
    return `${NEXATLAS_API_BASE}/userChart`
  }

  async sync(request: UserChartSyncRequest): Promise<Result<UserChartSyncResponse>> {
    const response = await this.httpClient.post<UserChartSyncResponse | UserChartsErrorBody>(
      `${this.getRemoteUrl()}/sync`,
      request,
      {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        },
        timeout: USER_CHARTS_SYNC_TIMEOUT_MS
      }
    )

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      const errorCode = (response.response as UserChartsErrorBody)?.error || 'SYNC_FAILED'
      return Result.fail(errorCode)
    }

    return Result.ok(response.response as UserChartSyncResponse)
  }

  // Uploads the processed tiles archive (ZIP of {z}/{x}/{y}.png) to the server so
  // any browser can later download it via `downloadProcessed`. Multipart `file`
  // field, mirroring the pilotapp `uploadProcessedZip`. Called from the sync
  // service for charts converted on this client.
  async uploadProcessed(chartId: string, archive: ArrayBuffer | Uint8Array): Promise<Result<void>> {
    const blob = new Blob([archive as BlobPart], { type: 'application/zip' })
    const formData = new FormData()
    formData.append('file', blob, `${chartId}-tiles.zip`)

    const response = await this.httpClient.post<unknown>(
      `${this.getRemoteUrl()}/${chartId}/processed`,
      formData as unknown as Record<string, any>,
      {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        },
        timeout: USER_CHARTS_UPLOAD_TIMEOUT_MS
      }
    )

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('UPLOAD_FAILED')
    }

    return Result.ok()
  }

  // Uploads the imported source file (PDF/TIFF/MBTiles) to the server so a chart
  // whose processed tiles are missing can later be re-hydrated by re-converting the
  // original (see EnsureUserChartTilesUseCase recovery), and so "download original"
  // works on any browser. Multipart `file` field, mirroring `uploadProcessed`.
  async uploadOriginal(chartId: string, original: Blob, fileName: string): Promise<Result<void>> {
    const formData = new FormData()
    formData.append('file', original, fileName)

    const response = await this.httpClient.post<unknown>(
      `${this.getRemoteUrl()}/${chartId}/original`,
      formData as unknown as Record<string, any>,
      {
        headers: {
          Authorization: `Token ${this.authState.getStateSnapshot().token}`
        },
        timeout: USER_CHARTS_UPLOAD_TIMEOUT_MS
      }
    )

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('UPLOAD_FAILED')
    }

    return Result.ok()
  }

  async downloadOriginal(chartId: string): Promise<Result<Blob>> {
    const response = await this.httpClient.get<Blob>(`${this.getRemoteUrl()}/${chartId}/original`, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      },
      responseType: 'blob'
    })

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('DOWNLOAD_FAILED')
    }

    return Result.ok(response.response)
  }

  // Downloads the processed tiles archive (ZIP of {z}/{x}/{y}.png) for a chart
  // that was synced from the server but has no tiles in the local in-memory
  // store. Mirrors the pilotapp sync download path.
  async downloadProcessed(chartId: string): Promise<Result<ArrayBuffer>> {
    const response = await this.httpClient.get<ArrayBuffer>(`${this.getRemoteUrl()}/${chartId}/processed`, {
      headers: {
        Authorization: `Token ${this.authState.getStateSnapshot().token}`
      },
      responseType: 'arraybuffer'
    })

    if (response.success === false) {
      return Result.fail('NETWORK_ERROR')
    }

    if (response.status < 200 || response.status >= 300) {
      return Result.fail('DOWNLOAD_FAILED')
    }

    return Result.ok(response.response)
  }
}
